import { Link, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { logoUrl } from "@/lib/assets";
import { APP_STORE_URL } from "@/lib/utils";

type ThemeMode = "light" | "dark";

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    if (typeof window === "undefined") return "light";
    const savedTheme = window.localStorage.getItem("quuil-theme");
    if (savedTheme === "dark" || savedTheme === "light") return savedTheme;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const router = useRouter();

  useEffect(() => {
    document.documentElement.dataset.theme = themeMode;
    window.localStorage.setItem("quuil-theme", themeMode);
  }, [themeMode]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [router.state.location.pathname]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape" && mobileMenuOpen) {
        setMobileMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    },
    [mobileMenuOpen]
  );

  useEffect(() => {
    if (!mobileMenuOpen) return;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown, mobileMenuOpen]);

  const handleLogoClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (router.state.location.pathname !== "/") return;
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
      setMobileMenuOpen(false);
    },
    [router.state.location.pathname]
  );

  return (
    <header className="bp-header">
      <div className="bp-header-inner">
        <Link to="/" aria-label="Quuil home" className="flex items-center" onClick={handleLogoClick}>
          <img src={logoUrl} alt="Quuil" width={180} height={72} className="bp-logo" />
        </Link>

        <nav className="bp-nav" aria-label="Main navigation">
          <NavLink to="/" label="Home" />
          <NavLink to="/gallery" label="The Inkwell" />
          <NavLink to="/showcase" label="Showcase" />
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
            Download
          </a>
        </nav>

        <button
          type="button"
          className="bp-theme-toggle"
          aria-label={`Switch to ${themeMode === "light" ? "dark" : "light"} mode`}
          aria-pressed={themeMode === "dark"}
          onClick={() => setThemeMode((mode) => (mode === "light" ? "dark" : "light"))}
        >
          <ThemeIcon mode={themeMode} />
        </button>

        <button
          ref={menuButtonRef}
          type="button"
          className="bp-mobile-button"
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
          onClick={() => setMobileMenuOpen((open) => !open)}
        >
          {mobileMenuOpen ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M3 7h18M3 12h18M3 17h18" />
            </svg>
          )}
        </button>
      </div>

      {mobileMenuOpen ? (
        <nav id="mobile-menu" className="bp-mobile-menu" aria-label="Mobile navigation">
          <MobileNavLink to="/" label="Home" onClick={() => setMobileMenuOpen(false)} />
          <MobileNavLink to="/gallery" label="The Inkwell" onClick={() => setMobileMenuOpen(false)} />
          <MobileNavLink to="/showcase" label="Showcase" onClick={() => setMobileMenuOpen(false)} />
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
            Download
          </a>
          <button
            type="button"
            className="bp-mobile-theme-toggle"
            aria-pressed={themeMode === "dark"}
            onClick={() => setThemeMode((mode) => (mode === "light" ? "dark" : "light"))}
          >
            {themeMode === "light" ? "Dark Mode" : "Light Mode"}
          </button>
        </nav>
      ) : null}
    </header>
  );
}

function NavLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      activeProps={{ "aria-current": "page" }}
    >
      {label}
    </Link>
  );
}

function MobileNavLink({ to, label, onClick }: { to: string; label: string; onClick?: () => void }) {
  return <Link to={to} onClick={onClick}>{label}</Link>;
}

function ThemeIcon({ mode }: { mode: ThemeMode }) {
  if (mode === "light") {
    return (
      <svg className="bp-theme-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M20.1 14.6A7.7 7.7 0 0 1 9.4 3.9 8.4 8.4 0 1 0 20.1 14.6Z" />
        <path d="M18.9 5.1 20 4m-1.1 4.9L20 10M15 4h1.5M15 10h1.5" />
      </svg>
    );
  }

  return (
    <svg className="bp-theme-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="3.4" />
      <path d="M12 2.8v2.1M12 19.1v2.1M4.9 4.9l1.5 1.5M17.6 17.6l1.5 1.5M2.8 12h2.1M19.1 12h2.1M4.9 19.1l1.5-1.5M17.6 6.4l1.5-1.5" />
    </svg>
  );
}
